import React from 'react';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { useTheme } from '../context/ThemeContext';
import Section from '../components/ui/Section';
import Card from '../components/ui/Card';

const About = () => {
  const { t } = useTranslation();
  const { theme } = useTheme();
  const isDark = theme === 'dark';
  const values = [
    {
      title: t('about.values.innovation.title'),
      description: t('about.values.innovation.description'),
    },
    {
      title: t('about.values.excellence.title'),
      description: t('about.values.excellence.description'),
    },
    {
      title: t('about.values.partnership.title'),
      description: t('about.values.partnership.description'),
    },
    {
      title: t('about.values.transparency.title'),
      description: t('about.values.transparency.description'),
    },
  ];

  return (
    <Section id="about" withFlyingElements={true}>
      <motion.div
        className="mb-12 md:mb-16 text-center"
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.6 }}
      >
        {/* Decorative divider */}
        <div className="flex items-center justify-center mb-6">
          <div className={`h-px w-12 ${isDark ? 'bg-afsc-gold/60' : 'bg-afsc-gold/80'}`} />
          <div className={`w-2 h-2 rounded-full mx-3 ${isDark ? 'bg-afsc-gold' : 'bg-afsc-gold'}`} />
          <div className={`h-px w-12 ${isDark ? 'bg-afsc-gold/60' : 'bg-afsc-gold/80'}`} />
        </div>
        <h2 className={`text-4xl md:text-5xl lg:text-6xl font-heading font-bold mb-4 ${isDark ? 'text-white' : 'text-afsc-charcoal'}`}>
          {t('about.title')} <span className="text-afsc-gold">{t('about.titleHighlight')}</span>
        </h2>
        <p className={`text-lg md:text-xl ${isDark ? 'text-white/70' : 'text-afsc-charcoal/70'} max-w-3xl mx-auto`}>
          {t('about.subtitle')}
        </p>
      </motion.div>
      <div className="max-w-4xl mx-auto mb-12">
        <motion.div
          initial={{ opacity: 0, y: 20, filter: 'blur(10px)' }}
          whileInView={{ opacity: 1, y: 0, filter: 'blur(0px)' }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className={`text-center space-y-4 text-lg leading-relaxed ${isDark ? 'text-white/80' : 'text-afsc-charcoal/70'}`}
        >
          <p>
            {t('about.description1')}
          </p>
          <p>
            {t('about.description2')}
          </p>
        </motion.div>
      </div>

      <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
        {values.map((value, index) => (
          <motion.div
            key={value.title}
            initial={{ opacity: 0, y: 30, filter: 'blur(10px)' }}
            whileInView={{ opacity: 1, y: 0, filter: 'blur(0px)' }}
            viewport={{ once: true }}
            transition={{ delay: index * 0.1, duration: 0.6 }}
          >
            <Card className={`${isDark ? 'bg-white/5' : 'bg-white/60'} backdrop-blur-sm border ${isDark ? 'border-afsc-soft-gray/20' : 'border-afsc-charcoal/10'} text-center h-full`}>
              <h3 className={`text-xl font-heading font-bold mb-3 ${isDark ? 'text-white' : 'text-afsc-charcoal'}`}>
                {value.title}
              </h3>
              <p className={`text-sm ${isDark ? 'text-white/70' : 'text-afsc-charcoal/70'}`}>
                {value.description}
              </p>
            </Card>
          </motion.div>
        ))}
      </div>
    </Section>
  );
};

export default About;

